import { ROWS, START_GROUND } from './constants.ts';
import { Rng } from './rng.ts';

export const T_EMPTY = 0;
export const T_SOLID = 1;
export const T_SPIKE = 2;
export const T_COIN = 3;

const MIN_H = 2;
const MAX_H = 7;
/** Column at which generation difficulty reaches 1.0 (~2 minutes of play). */
const DIFFICULTY_COLS = 700;

/**
 * Endless level, generated left to right in "segments" (flat run, pit, spikes…)
 * from a single seeded PRNG stream. Columns are generated lazily on first
 * access, but always in the same order, so any two Level instances with the
 * same seed are identical — nothing about the level ever goes over the wire.
 */
export class Level {
  readonly seed: number;
  private readonly cols: Uint8Array[] = [];
  private readonly rng: Rng;
  private h = START_GROUND;

  constructor(seed: number) {
    this.seed = seed >>> 0;
    this.rng = new Rng(this.seed);
    this.ground(24);
  }

  get generated(): number {
    return this.cols.length;
  }

  tile(c: number, r: number): number {
    if (c < 0) return T_SOLID;
    if (r < 0 || r >= ROWS) return T_EMPTY;
    while (c >= this.cols.length) this.segment();
    return this.cols[c][r];
  }

  difficulty(col = this.cols.length): number {
    return Math.min(1.25, col / DIFFICULTY_COLS);
  }

  private column(groundH: number): Uint8Array {
    const col = new Uint8Array(ROWS);
    for (let r = ROWS - groundH; r < ROWS; r++) col[r] = T_SOLID;
    this.cols.push(col);
    return col;
  }

  private ground(n: number, h = this.h): void {
    for (let i = 0; i < n; i++) this.column(h);
  }

  private pit(n: number): number {
    const start = this.cols.length;
    for (let i = 0; i < n; i++) this.column(0);
    return start;
  }

  private coin(c: number, r: number): void {
    if (r >= 0 && r < ROWS && this.cols[c][r] === T_EMPTY) this.cols[c][r] = T_COIN;
  }

  private segment(): void {
    const R = this.rng;
    const d = this.difficulty();
    const dc = Math.min(d, 1);
    const landing = Math.max(1, 3 - Math.round(dc * 2));

    const options: [string, number][] = [
      ['flat', 3 - 2 * dc],
      ['pit', 2 + d],
      ['step', 1.5],
      ['spikes', 1 + 2 * d],
      ['pillar', 1],
      ['islands', d > 0.25 ? 1 + d : 0],
      ['spikepit', d > 0.5 ? 1.5 * d : 0],
      ['spikestep', d > 0.4 ? d : 0],
    ];
    let total = 0;
    for (const [, w] of options) total += w;
    let roll = R.next() * total;
    let kind = options[0][0];
    for (const [k, w] of options) {
      if ((roll -= w) < 0) {
        kind = k;
        break;
      }
    }

    switch (kind) {
      case 'flat': {
        const len = R.int(2, Math.max(3, Math.round(7 - 4 * dc)));
        const start = this.cols.length;
        this.ground(len);
        if (R.chance(0.3)) for (let c = start; c < start + len; c++) this.coin(c, ROWS - this.h - 2);
        break;
      }
      case 'pit': {
        let w = R.int(2, 2 + Math.round(2 * dc));
        const newH = clamp(this.h + R.int(-1, 1), MIN_H, MAX_H);
        if (newH > this.h) w = Math.min(w, 3);
        const start = this.pit(w);
        if (R.chance(0.4)) this.coin(start + (w >> 1), ROWS - this.h - 3);
        this.h = newH;
        this.ground(landing + 1);
        break;
      }
      case 'step': {
        this.h = clamp(this.h + R.pick([-2, -1, 1, 2]), MIN_H, MAX_H);
        this.ground(R.int(2, 4));
        break;
      }
      case 'spikes': {
        this.ground(1);
        const n = R.int(1, 1 + Math.round(2 * dc));
        for (let i = 0; i < n; i++) this.column(this.h)[ROWS - this.h - 1] = T_SPIKE;
        if (R.chance(0.3)) this.coin(this.cols.length - 1 - (n >> 1), ROWS - this.h - 4);
        this.ground(landing + 1);
        break;
      }
      case 'pillar': {
        const ph = R.int(1, 2);
        const w = R.int(1, 2);
        for (let i = 0; i < w; i++) this.column(this.h + ph);
        this.ground(landing);
        break;
      }
      case 'islands': {
        const n = R.int(2, 3);
        for (let i = 0; i < n; i++) {
          this.pit(R.int(2, 3));
          this.h = clamp(this.h + R.int(-1, 1), MIN_H, MAX_H);
          const bw = R.int(2, d > 0.9 ? 2 : 3);
          for (let j = 0; j < bw; j++) this.column(0)[ROWS - this.h] = T_SOLID;
          if (R.chance(0.3)) this.coin(this.cols.length - 1, ROWS - this.h - 2);
        }
        this.pit(R.int(2, 3));
        this.h = clamp(this.h + R.int(-1, 0), MIN_H, MAX_H);
        this.ground(landing + 1);
        break;
      }
      case 'spikepit': {
        this.ground(1);
        this.column(this.h)[ROWS - this.h - 1] = T_SPIKE;
        this.pit(R.int(2, 3));
        this.ground(landing + 1);
        break;
      }
      case 'spikestep': {
        this.ground(1);
        this.h = clamp(this.h + 1, MIN_H, MAX_H);
        this.column(this.h)[ROWS - this.h - 1] = T_SPIKE;
        this.ground(landing + 1);
        break;
      }
    }
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
